import { CSS_SHORTHAND_MAPPINGS, ShortHandExpansionDef } from './constants';

/**
 * Pre-computed map of atomic properties to their parent shorthands.
 * Includes both direct parents and multi-level ancestor shorthands.
 *
 * @example
 * {
 *   'borderLeftColor': ['borderLeft', 'borderColor', 'border'],
 *   'fontSize': ['font'],
 *   'borderTopWidth': ['borderTop', 'borderWidth', 'border']
 * }
 *
 * Performance: O(1) lookup instead of O(n) iteration through CSS_SHORTHAND_MAPPINGS.
 */
export const ATOMIC_TO_PARENTS_MAP: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};

  // First pass: Build direct parent relationships
  Object.entries(CSS_SHORTHAND_MAPPINGS).forEach(([shorthand, expansions]) => {
    expansions.forEach((expansion) => {
      if (!map[expansion.propertyName]) {
        map[expansion.propertyName] = [];
      }
      map[expansion.propertyName].push(shorthand);
    });
  });

  // Second pass: Add multi-level parents (grandparents, great-grandparents, etc.)
  const getAllParents = (
    property: string,
    visited = new Set<string>(),
  ): string[] => {
    if (visited.has(property)) {
      return [];
    }
    visited.add(property);

    const directParents = map[property] || [];
    const allParents = [...directParents];

    directParents.forEach((parent) => {
      allParents.push(...getAllParents(parent, visited));
    });

    return allParents;
  };

  // Expand all atomic properties to include all ancestor shorthands
  Object.keys(map).forEach((property) => {
    map[property] = [...new Set(getAllParents(property))];
  });

  return map;
})();

/**
 * Get all parent shorthands for a property (including multi-level parents).
 *
 * @param property - The CSS property name
 * @returns Array of parent shorthand names, or empty array if property has no parents
 *
 * @example
 * getParentShorthands('borderLeftColor')
 * // => ['borderLeft', 'borderColor', 'border']
 *
 * getParentShorthands('fontSize')
 * // => ['font']
 *
 * getParentShorthands('color')
 * // => []
 */
export function getParentShorthands(property: string): string[] {
  return ATOMIC_TO_PARENTS_MAP[property] || [];
}

/**
 * Check if a property is a shorthand.
 *
 * @param property - The CSS property name
 * @returns true if the property is a shorthand, false otherwise
 *
 * @example
 * isShorthand('border') // => true
 * isShorthand('font') // => true
 * isShorthand('borderLeftColor') // => false
 * isShorthand('fontSize') // => false
 */
export function isShorthand(property: string): boolean {
  return CSS_SHORTHAND_MAPPINGS[property] !== undefined;
}

/**
 * Get atomic property names of a shorthand.
 *
 * @param shorthand - The shorthand property name
 * @returns Array of atomic property names, or empty array if not a shorthand
 *
 * @example
 * getShorthandAtomics('border')
 * // => ['borderWidth', 'borderStyle', 'borderColor']
 *
 * getShorthandAtomics('font')
 * // => ['fontSize', 'fontStyle', 'fontWeight', 'fontFamily', 'fontVariant', 'fontStretch', 'lineHeight']
 *
 * getShorthandAtomics('fontSize')
 * // => []
 */
export function getShorthandAtomics(shorthand: string): string[] {
  const expansions = CSS_SHORTHAND_MAPPINGS[shorthand];
  if (!expansions) {
    return [];
  }
  return expansions.map((exp) => exp.propertyName);
}

/**
 * Get all possible CSS properties that could be set in the Document Model (DM).
 *
 * This function receives CSS property types and returns all possible properties that might
 * exist in the DM, including both the original properties and any atomic properties from
 * shorthand expansions. This is used to determine which properties to check when filtering
 * or validating styles.
 *
 * @param cssPropertyTypes - Array of CSS property type names (can include shorthands and atomics)
 * @returns Set of all possible CSS property types that could be set in the DM
 *
 * @example
 * getPossibleCssProperties(['border'])
 * // => Set(['border', 'borderWidth', 'borderStyle', 'borderColor'])
 *
 * @example
 * getPossibleCssProperties(['borderWidth', 'color'])
 * // => Set(['borderWidth', 'color'])
 *
 * @example
 * getPossibleCssProperties(['font', 'fontSize'])
 * // => Set(['font', 'fontSize', 'fontStyle', 'fontWeight', 'fontFamily', 'fontVariant', 'fontStretch', 'lineHeight'])
 */
export function getPossibleCssProperties(
  cssPropertyTypes: string[],
): Set<string> {
  const possibleCssProperties = new Set<string>();

  cssPropertyTypes.forEach((cssProperty) => {
    possibleCssProperties.add(cssProperty);

    getShorthandAtomics(cssProperty).forEach((atomic) => {
      possibleCssProperties.add(atomic);
    });
  });

  return possibleCssProperties;
}

/**
 * Get atomic properties of a shorthand with their CSS property types.
 *
 * @param shorthand - The shorthand property name
 * @returns Array of expansion definitions, or empty array if not a shorthand
 *
 * @example
 * getShorthandExpansions('border')
 * // => [
 * //   { propertyName: 'borderWidth', cssPropertyType: 'borderWidth' },
 * //   { propertyName: 'borderStyle', cssPropertyType: 'borderStyle' },
 * //   { propertyName: 'borderColor', cssPropertyType: 'borderColor' }
 * // ]
 */
export function getShorthandExpansions(
  shorthand: string,
): ShortHandExpansionDef[] {
  return CSS_SHORTHAND_MAPPINGS[shorthand] || [];
}
